<template>
	<nav ref="nav" v-bind="$attrs" class="omapi-dropdown omapi-dropdown__button">
		<core-button
			ref="button"
			color="white"
			size="icon"
			:link-style="linkStyle"
			class="omapi-dropdown__toggle"
			@click.prevent="toggle()"
		>
			<i v-if="icon" :class="iconClasses" alt=""></i>
			<!-- @slot Button Text/Content -->
			<slot v-else>&hellip;</slot>
		</core-button>
		<div v-show="!hidden" class="omapi-dropdown__container" :style="style">
			<!-- @slot Dropdown List -->
			<ul>
				<!-- Should be <li><a></a></li> format -->
				<slot name="list"></slot>
			</ul>
		</div>
	</nav>
</template>

<script lang="js">
/**
 * Dropdown Button
 *
 * @since 2.0.0
 */
export default {
	props: {
		/**
		 * Icon
		 */
		icon: {
			type: String,
			default: '',
		},
		/**
		 * Width
		 */
		width: {
			type: Number,
			default: null,
		},
		/**
		 * Link Style
		 */
		linkStyle: {
			type: Boolean,
			default: false,
		},
		/**
		 * Whether dropdown is open by default.
		 */
		opened: Boolean,
		/**
		 * Whether dropdown is triggered open.
		 */
		open: Boolean,
		/**
		 * Whether dropdown will stay open unless clicked closed.
		 */
		keepOpen: {
			type: Boolean,
			default: false,
		},
		/**
		 * How dropdown should align under the button. Default is
		 * aligned to the left.
		 */
		align: {
			type: String,
			default: 'left',
		},
	},
	data() {
		return {
			hidden: true,
			navParent: null,
			button: null,
			preventClose: false,
		};
	},
	computed: {
		style() {
			if (!this.navParent || !this.button) {
				return {};
			}

			const parentWidth = this.width || this.navParent.offsetWidth;
			const style = {
				width: `${parentWidth}px`,
			};

			switch (this.align) {
				// left is the default.
				case 'left': {
					style.left = 0;
					break;
				}
				case 'center': {
					style.left = `-${Math.floor((parentWidth - this.button.offsetWidth) / 2)}px`;
					break;
				}
				case 'right': {
					style.right = 0;
					break;
				}
			}

			return style;
		},
		/**
		 * Icon Classes
		 *
		 * @returns {string} The icon classes or empty string
		 */
		iconClasses() {
			return this.icon ? `fa fa-${this.icon}` : '';
		},
	},
	watch: {
		/**
		 * Listens for open trigger.
		 *
		 * @since  2.0.0
		 *
		 * @param {boolean} opened Whether triggered open.
		 *
		 * @returns {void}
		 */
		open(opened) {
			if (opened) {
				this.preventClose = true;
				this.toggle(false);
				setTimeout(() => {
					this.preventClose = false;
				}, 200);
			} else {
				this.toggle(true);
			}
		},
		/**
		 * Listens for hidden being toggled.
		 *
		 * @since  2.0.0
		 *
		 * @param {boolean} hidden Whether hidden.
		 *
		 * @returns {void}
		 */
		hidden(hidden) {
			this.$emit(hidden ? 'close' : 'open');
			if (hidden) {
				this.preventClose = false;
			}
		},
	},
	mounted() {
		window.addEventListener('click', this.maybeClose);
		if (this.opened || this.open) {
			this.toggle();
		}
	},
	beforeDestroy() {
		window.removeEventListener('click', this.maybeClose);
	},
	methods: {
		/**
		 * Toggle Dropdown Visability
		 *
		 * @param {boolean} hide Explicit toggle to hide (or not).
		 *
		 * @returns {void}
		 */
		toggle(hide = null) {
			const navParent = this.$get('$refs.nav.parentElement');
			if (!this.navParent && navParent) {
				this.navParent = navParent;
			}

			const button = this.$get('$refs.button.$refs.button');
			if (!this.button && button) {
				this.button = button;
			}

			this.hidden = null !== hide ? Boolean(hide) : !this.hidden;
		},
		/**
		 * Close when clicking outside our elements (if keepOpen isn't true)
		 *
		 * @param {object} evt The event object.
		 *
		 * @returns {void}
		 */
		maybeClose(evt) {
			if (!this.hidden && !this.keepOpen && !this.preventClose) {
				const nav = this.$get('$refs.nav');
				let isMe = evt.target === nav;
				if (!isMe && nav) {
					isMe = nav.contains(evt.target);
				}
				if (!isMe) {
					this.toggle();
				}
			}
		},
	},
};
</script>

<style scoped lang="scss"></style>
